import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useI18n, LANGS } from "@/lib/i18n";
import { useEffect, useRef, useState } from "react";
import { Menu, X, User as UserIcon, LogIn, Globe, ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";

const LOGO_URL = "/branding/logo-blue.png";

export default function Header({ transparent = false }) {
  const { t, lang, setLang } = useI18n();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  function scrollToAnchor(id) {
    return (e) => {
      e.preventDefault();
      setMobileOpen(false);
      if (location.pathname === "/") {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate(`/#${id}`);
      }
    };
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onDown = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Hero-transparent style only applies on desktop (md+). On mobile the header is always solid white.
  const onHeroTransparent = transparent && !scrolled;
  const linkBase = "text-sm font-medium transition-colors";
  const linkColor = onHeroTransparent
    ? "text-white/90 hover:text-white"
    : "text-[#0A0F2C] hover:text-[#2D5BFF]";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all bg-white border-b border-gray-200/70 ${
        onHeroTransparent ? "xl:bg-transparent xl:border-transparent xl:backdrop-blur-0" : "xl:bg-white/85 xl:backdrop-blur-md"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8 h-20 xl:h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link" aria-label="Mopper home">
          <img
            src={LOGO_URL}
            alt="Mopper"
            className={`h-11 xl:h-14 w-auto object-contain ${onHeroTransparent ? "xl:brightness-0 xl:invert" : ""}`}
            loading="eager"
            decoding="async"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          <Link to="/services" className={`${linkBase} ${linkColor} px-3 py-2 rounded-lg hover:bg-gray-100/60`} data-testid="nav-services-top">
            {t.nav.services}
          </Link>
          <Link to="/#how-it-works" onClick={scrollToAnchor("how-it-works")} className={`${linkBase} ${linkColor} px-3 py-2 rounded-lg hover:bg-gray-100/60`} data-testid="nav-how">
            {t.nav.how}
          </Link>
          <Link to="/zones" className={`${linkBase} ${linkColor} px-3 py-2 rounded-lg hover:bg-gray-100/60`} data-testid="nav-zones">
            {t.nav.zones}
          </Link>
          <NavLink to="/professionnels" className={({ isActive }) => `${linkBase} ${linkColor} px-3 py-2 rounded-lg hover:bg-gray-100/60 ${isActive ? "font-semibold" : ""}`} data-testid="nav-business">
            {t.nav.business}
          </NavLink>
          <NavLink to="/a-propos" className={({ isActive }) => `${linkBase} ${linkColor} px-3 py-2 rounded-lg hover:bg-gray-100/60 ${isActive ? "font-semibold" : ""}`} data-testid="nav-about">
            {t.nav.about}
          </NavLink>
          <NavLink to="/aide" className={({ isActive }) => `${linkBase} ${linkColor} px-3 py-2 rounded-lg hover:bg-gray-100/60 ${isActive ? "font-semibold" : ""}`} data-testid="nav-help">
            {t.nav.help}
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/devis"
            className={`hidden xl:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-all ${
              onHeroTransparent
                ? "bg-[#FFD84D] text-[#0A0F2C] hover:bg-white"
                : "bg-[#2D5BFF] text-white hover:bg-[#0A0F2C]"
            }`}
            data-testid="header-quote-btn"
          >
            {t.nav.quote}
          </Link>
          {user ? (
            <Link
              to="/espace-client"
              className={`hidden xl:inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-all ${
                onHeroTransparent
                  ? "bg-white text-[#2D5BFF] hover:bg-gray-100"
                  : "bg-[#0A0F2C] text-white hover:bg-[#2D5BFF]"
              }`}
              data-testid="header-account-btn"
            >
              <UserIcon size={14} />
              <span className="max-w-[120px] truncate">{user.name?.split(" ")[0] || t.auth.header.account}</span>
            </Link>
          ) : (
            <Link
              to="/connexion"
              className={`hidden xl:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                onHeroTransparent
                  ? "bg-white/15 text-white hover:bg-white/25 border border-white/20"
                  : "bg-gray-100 text-[#0A0F2C] hover:bg-gray-200"
              }`}
              data-testid="header-login-btn"
            >
              <LogIn size={14} /> {t.auth.header.login}
            </Link>
          )}

          <div
            ref={langRef}
            className="relative hidden xl:block"
            data-testid="lang-switcher"
          >
            <button
              onClick={() => setLangOpen((v) => !v)}
              className={`flex items-center gap-1.5 rounded-full pl-3.5 pr-3 py-2 text-sm font-bold transition-all ${
                onHeroTransparent ? "bg-white/15 text-white hover:bg-white/25" : "bg-gray-100 text-[#0A0F2C] hover:bg-gray-200"
              }`}
              aria-haspopup="listbox"
              aria-expanded={langOpen}
              data-testid="lang-dropdown-toggle"
            >
              <Globe size={14} className="opacity-80" />
              <span>{(LANGS.find((l) => l.code === lang) || LANGS[0]).label}</span>
              <ChevronDown size={13} className={`transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div
                className="absolute right-0 mt-2 w-36 rounded-2xl bg-white shadow-[0_20px_45px_-15px_rgba(10,15,44,0.35)] border border-gray-100 p-1.5 z-50 origin-top-right animate-in fade-in slide-in-from-top-1 duration-150"
                role="listbox"
                data-testid="lang-dropdown-menu"
              >
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`w-full flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                      lang === l.code ? "bg-[#EAF0FF] text-[#2D5BFF]" : "text-[#0A0F2C] hover:bg-gray-50"
                    }`}
                    role="option"
                    aria-selected={lang === l.code}
                    data-testid={`lang-${l.code}`}
                  >
                    {l.label}
                    {lang === l.code && <span className="w-1.5 h-1.5 rounded-full bg-[#2D5BFF]" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className="xl:hidden p-2 rounded-full text-[#0A0F2C]"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden bg-white border-t border-gray-200 px-4 py-4 space-y-1" data-testid="mobile-menu">
          <Link to="/services" className="block py-2 text-[#0A0F2C] font-medium" data-testid="mobile-nav-services">{t.nav.services}</Link>
          <Link to="/#how-it-works" onClick={scrollToAnchor("how-it-works")} className="block py-2 text-[#0A0F2C] font-medium" data-testid="mobile-nav-how">{t.nav.how}</Link>
          <Link to="/zones" className="block py-2 text-[#0A0F2C] font-medium" data-testid="mobile-nav-zones">{t.nav.zones}</Link>
          <Link to="/professionnels" onClick={() => setMobileOpen(false)} className="block py-2 text-[#0A0F2C] font-medium" data-testid="mobile-nav-business">{t.nav.business}</Link>
          <Link to="/a-propos" onClick={() => setMobileOpen(false)} className="block py-2 text-[#0A0F2C] font-medium" data-testid="mobile-nav-about">{t.nav.about}</Link>
          <Link to="/aide" onClick={() => setMobileOpen(false)} className="block py-2 text-[#0A0F2C] font-medium" data-testid="mobile-nav-help">{t.nav.help}</Link>
          <Link
            to="/devis"
            onClick={() => setMobileOpen(false)}
            className="mt-2 flex items-center justify-center gap-2 py-3 rounded-full bg-[#2D5BFF] text-white font-bold"
            data-testid="mobile-nav-quote"
          >
            {t.nav.quote}
          </Link>
          <div className="pt-3 border-t border-gray-100">
            {user ? (
              <Link to="/espace-client" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-2 text-[#2D5BFF] font-bold" data-testid="mobile-nav-espace">
                <UserIcon size={16} /> {t.auth.header.account} ({user.name?.split(" ")[0]})
              </Link>
            ) : (
              <Link to="/connexion" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-2 text-[#2D5BFF] font-bold" data-testid="mobile-nav-login">
                <LogIn size={16} /> {t.auth.header.loginRegister}
              </Link>
            )}
          </div>
          <div className="flex items-center gap-2 pt-2">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  lang === l.code ? "bg-[#2D5BFF] text-white" : "bg-gray-100 text-[#0A0F2C]"
                }`}
                data-testid={`mobile-lang-${l.code}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
